import { ApolloProvider } from "@apollo/client/react";
import React, { useMemo, useRef } from "react";
import { useApolloSettings } from "../contexts/global/ApolloSettingsContext";
import type { DateSettings } from "./apolloClient";
import apolloClient from "./apolloClient";
import { useEvictAggregationsOnStructureChange } from "./useEvictAggregationsOnStructureChange";

const TenantApolloProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    getTokenSilently,
    overrideTenant,
    workspaceId,
    dateSettings,
    apiUrl,
  } = useApolloSettings();

  // Keep a ref so pitLink reads the latest dateSettings on each request without
  // recreating the Apollo client (which would flush the cache) on every PIT date change.
  const dateSettingsRef = useRef<DateSettings>(dateSettings);
  dateSettingsRef.current = dateSettings;

  // Only recreate client when structurally significant values change.
  // dateSettings is intentionally excluded — the link reads it via ref instead.
  const client = useMemo(() => {
    return apolloClient({
      getTokenSilently,
      ...(overrideTenant !== undefined && { overrideTenant }),
      ...(workspaceId !== undefined && { workspaceId }),
      getDateSettings: () => dateSettingsRef.current,
      apiUrl,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTokenSilently, overrideTenant, workspaceId, apiUrl]);

  useEvictAggregationsOnStructureChange(client);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default TenantApolloProvider;
